import * as moment from 'moment';
import { utils, write } from 'xlsx';
import type { WorkSheet } from 'xlsx';
import { tr } from '@util/tr';
import { formatUtcTimestampRome } from '@util/utc-timestamp';
import { locale } from '@util/locale';
import { WinesuitePayoutRow } from '@services/dws/ws-payments.service';
import { PaymentsAdvancedExport } from './payments-advanced-export';

/** List API page size for export (larger than UI paginator — fewer round trips). */
export const EXPORT_PAGE_SIZE = 500;

export type PayoutsListExportFormat = 'csv' | 'xlsx';

const MONETARY_EXCEL_NUMFMT = '#,##0.00';
const AMOUNT_COL_INDEX = 3;

const uiLocaleTag = (): string => {
  const iso = (locale().locale || 'it').toLowerCase();
  const map: Record<string, string> = {
    it: 'it-IT',
    en: 'en-GB',
    de: 'de-DE',
    fr: 'fr-FR',
    es: 'es-ES',
    pt: 'pt-PT',
    el: 'el-GR',
    hr: 'hr-HR',
  };
  return map[iso] ?? iso;
};

export const formatPayoutExportDate = (unix?: number): string => {
  if (!unix) {
    return '—';
  }
  return formatUtcTimestampRome(new Date(unix * 1000)) || '—';
};

export const payoutExportStatusLabel = (p: WinesuitePayoutRow): string => {
  if (p?.status === 'paid') {
    return tr('payments.payouts.statusPaid');
  }
  return tr('payments.payouts.statusInTransit');
};

export const formatPayoutExportAmountDisplay = (cents: number, currency = 'eur'): string => {
  return (cents / 100).toLocaleString(uiLocaleTag(), {
    style: 'currency',
    currency: (currency || 'eur').toUpperCase(),
    minimumFractionDigits: 2,
  });
};

export const payoutExportHeaders = (): string[] => [
  tr('payments.payouts.colDate'),
  tr('payments.payouts.colStatus'),
  tr('payments.payouts.colDestination'),
  tr('payments.payouts.colAmount'),
  tr('payments.payouts.filterPayoutId'),
];

export const mapPayoutRowToExportCells = (row: WinesuitePayoutRow): string[] => [
  formatPayoutExportDate(row.created),
  payoutExportStatusLabel(row),
  row.destination ?? '',
  formatPayoutExportAmountDisplay(row.amount ?? 0, row.currency),
  row.tracking_id ?? row.id ?? '',
];

const escapeCsv = (value: unknown): string => {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const applyMonetaryNumberFormat = (sheet: WorkSheet, moneyColIndexes: number[]): void => {
  if (!sheet['!ref'] || !moneyColIndexes.length) {
    return;
  }
  const range = utils.decode_range(sheet['!ref']);
  for (const c of moneyColIndexes) {
    if (c < range.s.c || c > range.e.c) {
      continue;
    }
    for (let r = range.s.r + 1; r <= range.e.r; r++) {
      const addr = utils.encode_cell({ r, c });
      const cell = sheet[addr];
      if (cell && cell.t === 'n' && typeof cell.v === 'number' && Number.isFinite(cell.v)) {
        cell.z = MONETARY_EXCEL_NUMFMT;
      }
    }
  }
};

export const prepareExportTranslations = (): void => {
  PaymentsAdvancedExport.prepareExportTranslations();
};

export const buildPayoutListExportFile = (
  rows: WinesuitePayoutRow[],
  format: PayoutsListExportFormat = 'csv',
): { blob: Blob; filename: string } | null => {
  if (!rows?.length) {
    return null;
  }

  prepareExportTranslations();
  const date = moment().format('YYYY-MM-DD');
  const headers = payoutExportHeaders();

  if (format === 'csv') {
    const csvRows: string[] = [headers.map(escapeCsv).join(',')];
    for (const row of rows) {
      csvRows.push(mapPayoutRowToExportCells(row).map(escapeCsv).join(','));
    }
    const blob = new Blob(['\uFEFF' + csvRows.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    return { blob, filename: `payouts_${date}.csv` };
  }

  const sheetData: (string | number)[][] = [headers];
  for (const row of rows) {
    const amountNum = (row.amount ?? 0) / 100;
    sheetData.push([
      formatPayoutExportDate(row.created),
      payoutExportStatusLabel(row),
      row.destination ?? '',
      Number.isFinite(amountNum) ? amountNum : '',
      row.tracking_id ?? row.id ?? '',
    ]);
  }

  const sheet = utils.aoa_to_sheet(sheetData);
  applyMonetaryNumberFormat(sheet, [AMOUNT_COL_INDEX]);
  sheet['!cols'] = headers.map(() => ({ wch: 18 }));

  const book = utils.book_new();
  utils.book_append_sheet(book, sheet, 'Payouts');
  const buffer = write(book, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  return { blob, filename: `payouts_${date}.xlsx` };
};
